import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../../lib/api.js';
import { toast } from '../../components/ui/Toast.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Badge } from '../../components/ui/Badge.js';
import { Spinner } from '../../components/ui/Spinner.js';
import type { InternalUser } from '@ember/types';

export default function AdminAccessPage() {
  const [users, setUsers] = useState<InternalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  function load() {
    setLoading(true);
    api
      // Response: { internalUsers: InternalUser[] }
      .get<{ internalUsers: InternalUser[] }>('/api/admin/internal-users')
      .then((r) => setUsers(r.internalUsers))
      .catch(() => toast.error('Failed to load admin users'))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    const name = newName.trim();
    if (!email || !name) return;
    setAdding(true);
    try {
      // POST /api/admin/internal-users — body: { email, name }
      await api.post('/api/admin/internal-users', { email, name });
      toast.success(`Granted admin access to ${email}`);
      setNewEmail('');
      setNewName('');
      load();
    } catch {
      toast.error('Failed to add admin user');
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(id: string, email: string) {
    if (!confirm(`Remove admin access for ${email}?`)) return;
    try {
      await api.delete(`/api/admin/internal-users/${id}`);
      toast.success(`Removed ${email}`);
      load();
    } catch {
      toast.error('Failed to remove admin user');
    }
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-neutral-100 mb-2">Admin Access</h1>
      <p className="text-sm text-neutral-400 mb-6">
        These email addresses are permitted to access the admin panel via Cloudflare Access.
        They must also be configured in your CF Access policy.
      </p>

      {/* Add user */}
      <form onSubmit={handleAdd} className="flex flex-col gap-2 mb-6">
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder="Display name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="w-36 shrink-0"
            required
          />
          <Input
            type="email"
            placeholder="admin@yourcompany.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="flex-1"
            required
          />
          <Button type="submit" isLoading={adding}>
            Grant
          </Button>
        </div>
      </form>

      {/* List */}
      {loading ? (
        <Spinner />
      ) : users.length === 0 ? (
        <p className="text-neutral-500 text-sm">No admin users.</p>
      ) : (
        <ul className="divide-y divide-neutral-800 rounded-xl border border-neutral-800 bg-neutral-900 overflow-hidden">
          {users.map((u) => (
            <li key={u.id} className="flex items-center justify-between px-4 py-3 gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-neutral-200 truncate">{u.name}</p>
                <p className="text-xs text-neutral-500 truncate">{u.email}</p>
                <p className="text-xs text-neutral-600">
                  Added {new Date(u.addedAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="info">Admin</Badge>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => void handleRemove(u.id, u.email)}
                >
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 rounded-xl border border-neutral-800 bg-neutral-900/50 p-4 text-sm text-neutral-500">
        <p className="font-medium text-neutral-400 mb-1">⚠️ Note</p>
        <p>
          This list controls app-level admin access. You must also configure matching
          email policies in your Cloudflare Access application to restrict the{' '}
          <code className="text-neutral-400">/admin/*</code> path.
        </p>
      </div>
    </div>
  );
}
